import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AlertDialog from 'components/UI/AlertDialog';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { FollowUpConfig } from 'services/types/followUpConfig';
import { NAVY } from 'styles/tokens';

import HandleFile from './components/handleFile';
import SurveysStepper from './components/SurveysStepper';
import SelectOrCreateFollowUp from './selectOrCreateFollowUp';
import { bodyTextSx, labelSx, pageContainerSx, sectionGap, sectionTitleSx } from './styles';

const STEPS = ['Elegir encuesta', 'Subir clientes'];

const Surveys = () => {
  const navigate = useNavigate();
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const businessUnitId = selectedBusinessUnitId && selectedBusinessUnitId > 0 ? selectedBusinessUnitId : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStep = searchParams.get('step') === '2' ? 2 : 1;
  const [followUpSuccessAlert, setFollowUpSuccessAlert] = useState(false);
  const [clientUploadSuccessAlert, setClientUploadSuccessAlert] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUpConfig | null>(null);

  const step = urlStep === 2 && selectedFollowUp ? 2 : 1;

  const goToStep = useCallback(
    (newStep: number, options?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('step', String(newStep));
          return next;
        },
        { replace: options?.replace }
      );
    },
    [setSearchParams]
  );

  // If the URL points at step 2 but no campaign has been chosen (e.g. direct
  // link, refresh, or browser back into a stale state), bounce back to step 1.
  useEffect(() => {
    if (urlStep === 2 && !selectedFollowUp) {
      goToStep(1, { replace: true });
    }
  }, [urlStep, selectedFollowUp, goToStep]);

  const handleSelectFollowUp = useCallback(
    (followUp: FollowUpConfig) => {
      setSelectedFollowUp(followUp);
      goToStep(2);
    },
    [goToStep]
  );

  const handleUploadSuccess = useCallback(
    (uploadedSource: string) => {
      if (!selectedFollowUp) {
        setSelectedFollowUp(null);
        goToStep(1);
        return;
      }
      // Navigate to the leads table pre-filtered by the survey identifier and the
      // source the user just uploaded under, so they immediately see those leads.
      const filterItems: Array<{ field: string; operator: string; value: string }> = [
        { field: 'whatsAppCampaign', operator: 'equals', value: selectedFollowUp.identifier },
      ];
      if (uploadedSource) {
        filterItems.push({ field: 'source', operator: 'equals', value: uploadedSource });
      }
      const params = new URLSearchParams();
      params.set('viewMode', 'table');
      params.set('filterModel', JSON.stringify({ items: filterItems }));
      setSelectedFollowUp(null);
      navigate(`/leads?${params.toString()}`);
    },
    [selectedFollowUp, goToStep, navigate]
  );

  const handleBackToStep1 = useCallback(() => {
    setSelectedFollowUp(null);
    goToStep(1);
  }, [goToStep]);

  return (
    <Box sx={pageContainerSx}>
      <AlertDialog
        openDialog={followUpSuccessAlert}
        setOpenDialog={setFollowUpSuccessAlert}
        header="Follow up creado!"
        msg="Follow up fue creado correctamente"
        cancelMsg="Cerrar"
      />
      <AlertDialog
        openDialog={clientUploadSuccessAlert}
        setOpenDialog={setClientUploadSuccessAlert}
        header="Clientes subidos!"
        msg="Clientes fueron subidos correctamente"
        cancelMsg="Cerrar"
      />
      {businessUnitId && <SurveysStepper activeStep={step - 1} steps={STEPS} />}
      {step === 1 && <SelectOrCreateFollowUp businessUnitId={businessUnitId} selectFollowUp={handleSelectFollowUp} />}
      {step === 2 && businessUnitId && selectedFollowUp && (
        <Box sx={{ maxWidth: 1000, width: '100%', mx: 'auto' }}>
          <Button
            onClick={handleBackToStep1}
            startIcon={<ArrowBackIcon />}
            sx={{
              color: NAVY,
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              textTransform: 'none',
              mb: sectionGap,
              '&:hover': { backgroundColor: 'transparent', opacity: 0.7 },
            }}
          >
            Volver a elegir encuesta
          </Button>
          <Box sx={{ mb: sectionGap }}>
            <Typography sx={labelSx}>Encuesta seleccionada</Typography>
            <Typography sx={{ ...sectionTitleSx, mt: 0.5 }}>{selectedFollowUp.identifier}</Typography>
            {selectedFollowUp.templateName && (
              <Typography sx={{ ...bodyTextSx, opacity: 0.65 }}>Plantilla: {selectedFollowUp.templateName}</Typography>
            )}
          </Box>
          <HandleFile
            businessUnitId={businessUnitId}
            outboundCampaignId={selectedFollowUp.identifier}
            onSuccess={handleUploadSuccess}
          />
        </Box>
      )}
    </Box>
  );
};

export default Surveys;
